<template>
    <div v-if="org" class="OrgView">
        <OrgViewAdmin 
            v-if="isAdminView" 
            :jobs="orgJobs" 
            :org="org" 
            :projects="orgProjects" 
            :skills="orgSkills" 
        />
        <OpportunityPageLayout
            v-else
            back-label="Späť na organizácie"
            :preview="preview || isOrgMember"
            :to="{
                name: 'organizacie',
                query: $route.query,
                hash: `#r-id-${$route.params.id}`
            }"
        >
            <OrgViewLayoutTop
                :jobs="orgJobs"
                :org="org"
                :preview="preview"
                :projects="orgProjects"
                :skills="orgSkills"
            />

            <template #middle>
                <div class="OrgViewLayoutMiddle">
                    <OpportunityPageButtons
                        :id="org.id"
                        fixed-on-mobile
                        :is-flexbox="isOrgMember || isOrgMemberComputed"
                        :org-name="org.title"
                        :org-type="org.type"
                        :preview="preview"
                        :title="org.title"
                        type="org"
                    >
                        <template v-if="isOrgMember || isOrgMemberComputed">
                            <OrgViewMemberButtons :org="org" />
                        </template>
                    </OpportunityPageButtons>
                </div>
            </template>

            <template #left>
                <div id="OrgViewLayoutLeft" class="space-y-6">
                    <OrgImageGallery
                        v-if="areGalleryImagesVisible || areGalleryVideosVisible"
                        :images="areGalleryImagesVisible ? org.galleryImages : []"
                        :videos="areGalleryVideosVisible ? org.galleryVideos : []"
                    />

                    <OrgMembers v-if="isOrgMember" :org="org" />
                </div>
            </template>

            <template #right>
                <div class="OrgViewLayoutRight">
                    <div v-if="org.desc && isOrgFieldVisible(org, 'desc')" class="mb-4 sm:mb-0">
                        <CHeading variant="2">{{ orgProfileHeadlineText }}</CHeading>

                        <CCardBox>
                            <span v-html="getOrgFieldFunction(org, 'desc')(org.desc)" />
                        </CCardBox>
                    </div>

                    <OrgOpportunitiesGroup
                        id="active-skills"
                        :class="{ 'pt-4 sm:pt-8 md:pt-10': org.desc }"
                        item-type="skill"
                        :items="orgSkills"
                        title="Vzdelávanie"
                    />

                    <OrgOpportunitiesGroup
                        id="active-jobs"
                        class="pt-4 sm:pt-8 md:pt-10"
                        item-type="job"
                        :items="orgJobs"
                        title="Pracovné ponuky"
                    />

                    <OrgOpportunitiesGroup
                        id="active-projects"
                        class="pt-4 sm:pt-8 md:pt-10"
                        item-type="project"
                        :items="orgProjects"
                        title="Dobrovoľnícke projekty"
                    />
                </div>
            </template>
        </OpportunityPageLayout>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { isWithinVolunteeringWeekVisibilityWindow } from '#shared/utils/volunteeringWeekDates'
import type { Org, OrgType } from '#shared/types/Org'
import type { Job } from '#shared/types/Job'
import { ProjectType } from '#shared/types/Project'
import type { Skill } from '#shared/types/Skill'
import type { Project } from '#shared/types/Project'
import { orgTypeProfileAboutText } from '#shared/types/dictionary'
import OpportunityPageLayout from '@/components/common/opportunity/OpportunityPageLayout.vue'
import OrgViewAdmin from '@/components/orgs/OrgViewAdmin.vue'
import OrgViewLayoutTop from '@/components/orgs/OrgViewLayoutTop.vue'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useFirebase } from '@/composables/firebase/useFirebase'

interface Props {
    org: Org
    isOrgMember?: boolean
    isAdminView?: boolean
    preview?: boolean
    // Public opportunities loaded on the server (/orgs/[id]), rendered without a client query
    opportunities?: {
        jobs: Job[]
        skills: Skill[]
        projects: Project[]
    }
}

const props = withDefaults(defineProps<Props>(), {
    isOrgMember: false,
    isAdminView: false,
    preview: false,
    opportunities: undefined
})

const orgJobs = ref<Job[]>(props.opportunities?.jobs ?? [])
const orgSkills = ref<Skill[]>(props.opportunities?.skills ?? [])
const orgProjects = ref<Project[]>(props.opportunities?.projects ?? [])

const { loadVisibleOpportunities, isOrgFieldVisible, getOrgFieldFunction, canEditOrgOpportunity } = useOrgs()
const { collections, auth } = useFirebase()

// Computed properties from extracted layout components
const areGalleryImagesVisible = computed(() => {
    return !!props.org.galleryImages?.length && isOrgFieldVisible(props.org, 'galleryImages')
})

const areGalleryVideosVisible = computed(() => {
    return !!props.org.galleryVideos?.length && isOrgFieldVisible(props.org, 'galleryVideos')
})

const orgProfileHeadlineText = computed(() => {
    return orgTypeProfileAboutText(props.org.type as OrgType)
})

// Check if current user is an org member (used in middle layout for buttons)
const isOrgMemberComputed = computed(() => {
    // `auth` is null during SSR
    return canEditOrgOpportunity(auth?.currentUser?.uid, props.org)
})

const shouldExposePrelaunchVolunteeringWeekProjects = computed(() => {
    return props.preview || props.isAdminView || props.isOrgMember || isOrgMemberComputed.value
})

const loadOrganizationJobs = async (): Promise<void> => {
    try {
        orgJobs.value = await loadVisibleOpportunities<Job>(collections.jobs, props.org.id)
    } catch (error) {
        console.error('Error loading organization jobs:', error)
        orgJobs.value = []
    }
}

const loadOrganizationSkills = async (): Promise<void> => {
    try {
        orgSkills.value = await loadVisibleOpportunities<Skill>(collections.skills, props.org.id)
    } catch (error) {
        console.error('Error loading organization skills:', error)
        orgSkills.value = []
    }
}

const loadOrganizationProjects = async (): Promise<void> => {
    try {
        const visibleProjects = await loadVisibleOpportunities<Project>(collections.projects, props.org.id)

        if (shouldExposePrelaunchVolunteeringWeekProjects.value || isWithinVolunteeringWeekVisibilityWindow()) {
            orgProjects.value = visibleProjects
            return
        }

        orgProjects.value = visibleProjects.filter((project) => project.type !== ProjectType.VOLUNTEERING_WEEK)
    } catch (error) {
        console.error('Error loading organization projects:', error)
        orgProjects.value = []
    }
}

const loadOrganizationOpportunities = async (): Promise<void> => {
    await Promise.all([
        loadOrganizationJobs(),
        loadOrganizationSkills(), 
        loadOrganizationProjects()
    ])
}

onMounted(async () => {
    // Server-provided opportunities are already the public set; only members need the
    // client query, which also returns pre-launch volunteering-week projects.
    if (props.opportunities && !shouldExposePrelaunchVolunteeringWeekProjects.value) return

    await loadOrganizationOpportunities()
})

// The public page learns about membership only after hydration (see pages/orgs/[id].vue)
watch(
    () => props.isOrgMember,
    (isOrgMember) => {
        if (isOrgMember && props.opportunities) loadOrganizationOpportunities()
    }
)
</script>
